import moment from './moment'
import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  forwardRef,
  useMemo,
  useCallback
} from 'react'
import PropTypes from 'prop-types'
import {
  contains,
  classNames,
  positionElement,
  useEventHandler,
  useSetState,
  useGlobalId,
  useUpdateRef,
  elementAttributes,
  Icon,
  iconProps
} from '@zambezi/sdk/common'
import DatePickerDropDown from './DatePickerDropDown'
import { FORMAT, useStartOfDay } from './utils'

/**
 * @type {import('./interfaces').AllowedFormat[]}
 */
const ALLOWED_FORMATS = [
  'DD-MM-YY',
  'DD-MMM-YY',
  'DD-MM-YYYY',
  'DD-MMM-YYYY',
  'DD/MM/YY',
  'DD/MMM/YY',
  'DD/MM/YYYY',
  'DD/MMM/YYYY',
  'DD MM YY',
  'DD MMM YY',
  'DD MM YYYY',
  'DD MMM YYYY',
  'DD.MM.YY',
  'DD.MMM.YY',
  'DD.MM.YYYY',
  'DD.MMM.YYYY',
  'YYYY-MM-DD',
  'YYYY-MMM-DD'
]
/**
 * @type {import('./interfaces').AllowedFormat}
 */
const MOBILE_FORMAT = 'YYYY-MM-DD' // value format of input type=date
const ALLOWED_MOBILE_FORMATS = [MOBILE_FORMAT]
/**
 * Create a React Date Picker component
 *
 * @param {import('./interfaces').DatePickerProps} props
 * @param {import('react').Ref<HTMLElement>} ref
 * @returns {JSX.Element}
 */
function datePicker (props, ref) {
  const {
    minDate: propsMinDate,
    maxDate: propsMaxDate,
    error: propsError,
    date: pDate,
    defaultDate,
    initialViewDate,
    dateFormat,
    isMobile,
    scrollIntoView,
    disabled,
    readOnly,
    required,
    openOnInputFocus,
    openOnIconFocus,
    keepDateInRange,
    placeholder,
    className,
    id,
    disableDayFn,
    onUserChange,
    onInputChange,
    onBlur,
    onFocus,
    onChange,
    onError,
    customErrorMsg: {
      invalidErrorMsg,
      rangeErrorMsg,
      minRangeErrorMsg,
      maxRangeErrorMsg,
      disableDayFnErrorMsg
    },
    ariaLabel
  } = props
  const propsDate = useMemo(
    () => sanitizeDate(pDate),
    [pDate && pDate.valueOf()]
  )
  const minDate = useMemo(
    () => sanitizeDate(propsMinDate),
    [propsMinDate && propsMinDate.valueOf()]
  )
  const maxDate = useMemo(
    () => sanitizeDate(propsMaxDate),
    [propsMaxDate && propsMaxDate.valueOf()]
  )
  const [
    {
      date,
      focusedDate,
      error,
      focus,
      displayDate,
      currentPicker,
      activeDescendant,
      inputValue,
      iconFocus
    },
    setState
  ] = useSetState({
    displayDate: '',
    inputValue: null,
    date: propsDate || sanitizeDate(defaultDate) || null,
    error: propsError || false,
    focus: false,
    iconFocus: false,
    currentPicker: null,
    focusedDate: initialViewDate,
    containerElement: null,
    activeDescendant: null
  })

  const startOfDayDate = useStartOfDay()
  const dropdownContainerId = useGlobalId('zb-date-picker-dropdown-')
  /** @type {import('react').RefObject<Element>} */
  const containerElementRef = useRef(null)
  const prevDate = useRef(date)
  const prevPropsDate = useRef(propsDate)
  const prevError = useRef(error)
  const dropdownContainerRef = useRef(null)
  const iconRef = useRef(null)
  const inputRef = useRef(null)
  const initialMount = useRef(true)
  const isShown = !!currentPicker
  let aboutToHide = null

  useUpdateRef(ref, containerElementRef)
  useLayoutEffect(onPickerChanged, [currentPicker])
  useEffect(onErrorChanged, [propsError])
  useEffect(onDateChanged, [date])
  useEventHandler('resize', handleResize, document.defaultView)
  useEffect(componentDidMount, [null])
  useEffect(componentWillReceiveProps, [
    propsDate,
    minDate,
    maxDate,
    disableDayFn
  ])
  useEffect(() => {
    !propsDate && setState({ focusedDate: initialViewDate })
  }, [propsDate, initialViewDate])
  const handleFocusChanged = useCallback(
    /**
     * @param {string} activeDescendant
     * @param {Date} newFocusedDate
     */
    function _handleFocusChanged (
      activeDescendant,
      newFocusedDate = focusedDate
    ) {
      setState({ activeDescendant, focusedDate: newFocusedDate })
    },
    []
  )

  return render()

  function showMonthPicker () {
    showPicker('month')
  }
  function hideMonthPicker () {
    hidePicker('month')
  }
  function showYearPicker () {
    showPicker('year')
  }
  function hideYearPicker () {
    hidePicker('year')
  }
  function show () {
    showPicker('day')
  }
  function hide () {
    hidePicker('day')
  }

  function componentDidMount () {
    initialMount.current = false
    setState({ containerElement: containerElementRef.current })
  }

  function componentWillReceiveProps () {
    const newDate = hasDateChanged(propsDate, prevPropsDate.current)
      ? propsDate
      : date
    const newFocusedDate = propsDate ? null : focusedDate
    commitDate(newDate, null, newFocusedDate)
    prevPropsDate.current = propsDate
  }

  function onPickerChanged () {
    if (isShown) {
      if (scrollIntoView) {
        scrollIntoViewIfNeeded(dropdownContainerRef.current)
      }
    }
  }

  function onErrorChanged () {
    if (propsError != null) {
      prevError.current = error
      setState({ propsError })
    }
  }

  function onDateChanged () {
    if (
      !initialMount.current &&
      (hasDateChanged(date, prevDate.current) ||
        (!error && !!prevError.current && !propsDate))
    ) {
      dispatchOnChange(date, inputValue)
    }
    prevDate.current = date
  }

  /**
   * @param {Element} element
   */
  function scrollIntoViewIfNeeded (element) {
    const documentElement = document.documentElement
    const elementClientRect = element.getBoundingClientRect()
    const elementOffsetTop =
      elementClientRect.top +
      window.pageYOffset +
      (element.offsetParent ? -element.offsetParent.offsetTop : 0)
    const requiredHeight = elementClientRect.top + elementClientRect.height

    if (elementOffsetTop < documentElement.scrollTop) {
      documentElement.scrollTop = elementOffsetTop
    } else if (requiredHeight > documentElement.clientHeight) {
      documentElement.scrollTop += requiredHeight - documentElement.clientHeight
    }
  }

  function getShownDate () {
    return focusedDate || date || startOfDayDate
  }

  function handleResize () {
    if (isShown) {
      positionElement(
        dropdownContainerRef.current,
        containerElementRef.current,
        {
          direction: 'bottom',
          align: 'left',
          offset: 0
        }
      )
    }
  }

  /**
   * @param {Date=} date
   */
  function dispatchUserChange (date) {
    if (date) {
      onUserChange(date)
    }
  }

  /**
   * @param {import('react').KeyboardEvent} evt
   */
  function handleKeyPress (evt) {
    if (evt.key === 'Enter') {
      const newDate = commit(evt.target.value)
      dispatchUserChange(newDate)
    }
  }

  /**
   * @param {import('react').ChangeEvent<HTMLInputElement>} evt
   */
  function handleChange (evt) {
    setState({
      displayDate: evt.target.value
    })
    onInputChange(evt.target.value)
  }

  /**
   * @param {import('react').FocusEvent<HTMLInputElement>} evt
   */
  function handleInputBlur (evt) {
    handleBlur(evt)
    const newDate = commit(evt.target.value)
    dispatchUserChange(newDate)
    setState({ focus: false })
  }

  function handleIconBlur (evt) {
    if (document.activeElement !== iconRef.current) {
      setState({ iconFocus: false })
    }
  }
  /**
   * @param {import('react').FocusEvent<HTMLInputElement>} evt
   */
  function handleInputFocus (evt) {
    handleFocus(evt)
    if (openOnInputFocus) {
      show()
    }
  }

  /**
   * @param {import('react').FocusEvent} evt
   */
  function handleIconFocus (evt) {
    setState({ iconFocus: document.activeElement === iconRef.current })

    if (openOnIconFocus) {
      show()
    }
  }

  /**
   * @param {import('react').FocusEvent} evt
   */
  function handleFocus (evt) {
    if (!focus) {
      setState({ focus: true })
      onFocus(evt)
    }
  }

  /**
   * @param {import('react').FocusEvent} evt
   */
  function handleBlur (evt) {
    const hideOnBlur = !openOnInputFocus && !openOnIconFocus && aboutToHide

    if ((!isInside(dropdownContainerRef, evt) || hideOnBlur) && focus) {
      hide()
      setState({ focus: false })
      onBlur(evt)
    }
  }

  /**
   * @param {Date} date
   */
  function handleSelect (date) {
    hide()
    !openOnIconFocus && iconRef.current && iconRef.current.focus()

    if (date !== null) {
      const newDate = commitDate(date)
      dispatchUserChange(newDate)
    }
  }

  /**
   * @param {number} month
   * @param {number} year
   */
  function handleMonthSelected (month, year) {
    hideMonthPicker()
    if (month !== null) {
      const focusedDate = moment(getShownDate())
        .year(year)
        .month(month)
        .toDate()
      const activeDescendant = `${dropdownContainerId}-${
        moment.localeData().monthsShort()[month]
      }`
      setState({ focusedDate, activeDescendant })
    }
  }

  /**
   * @param {number} year
   */
  function handleYearSelected (year) {
    hideYearPicker()
    if (year !== null) {
      const focusedDate = moment(getShownDate()).year(year).toDate()
      const activeDescendant = `${dropdownContainerId}-${focusedDate.getFullYear()}`
      setState({
        focusedDate,
        activeDescendant
      })
    }
  }

  /**
   * @param {string} value
   */
  function commit (value) {
    const allowableFormat = isMobile ? ALLOWED_MOBILE_FORMATS : ALLOWED_FORMATS
    const parsedDate = value
      ? moment(value, allowableFormat, true).toDate()
      : null

    return commitDate(parsedDate, value)
  }

  /**
   * @param {Date} date
   * @param {string=} inputValue
   * @param {Date=} focusedDate
   */
  function commitDate (date, inputValue = null, focusedDate = null) {
    const dateInRange = keepDateInRange
      ? ensureDateIsInRange(sanitizeDate(date))
      : sanitizeDate(date)
    const newDate = date ? dateInRange : null
    const errorMessage = getErrorMessage(newDate)

    prevError.current = error
    const newError = propsError != null ? propsError : !!errorMessage
    if (errorMessage) {
      onError({ message: errorMessage }, inputValue)
      setState({
        error: newError,
        inputValue,
        date: moment(date).isValid() ? newDate : null
      })
    } else {
      const newFormat = isMobile ? MOBILE_FORMAT : dateFormat
      setState({
        date: newDate,
        displayDate: newDate ? moment(newDate).format(newFormat) : '',
        focusedDate,
        inputValue,
        error: newError
      })
    }

    return newDate
  }

  /**
   * @param {Date} date
   */
  function getErrorMessage (date) {
    let errorMessage = ''
    if (date) {
      const validators = [
        validateDateFormat,
        validateDate,
        validateDateAvailability,
        validateDateInRange
      ]
      for (let i = 0; !errorMessage && i < validators.length; i++) {
        errorMessage = validators[i](date)
      }
    } else if (required) {
      errorMessage = 'Date is required'
    }
    return errorMessage
  }

  /**
   * @param {Date} date
   */
  function validateDate (date) {
    return !moment(date).isValid()
      ? invalidErrorMsg || 'Date is not in a valid format'
      : ''
  }

  /**
   * @param {Date} date
   */
  function validateDateAvailability (date) {
    if (disableDayFn && disableDayFn(date)) {
      return disableDayFnErrorMsg || `${date} is not an enabled date`
    }
    return false
  }

  /**
   * @param {Date} date
   */
  function ensureDateIsInRange (date) {
    const isOutOfRange = isDateOutOfRange(date)

    if (isOutOfRange < 0) return minDate
    if (isOutOfRange > 0) return maxDate
    return date
  }

  /**
   * @param {Date} date
   */
  function validateDateInRange (date) {
    const isOutOfRange = isDateOutOfRange(date)

    if (!isOutOfRange) return ''

    if (minDate && maxDate) {
      return (
        rangeErrorMsg || `${date} must be between ${minDate} and ${maxDate}`
      )
    }

    if (maxDate) return maxRangeErrorMsg || `${date} must be before ${maxDate}`

    return minRangeErrorMsg || `${date} must be after ${minDate}`
  }

  function validateDateFormat () {
    if (ALLOWED_FORMATS.indexOf(dateFormat) !== -1) return ''
    return 'DateFormat should be valid'
  }

  /**
   * @param {Date} date
   */
  function isDateOutOfRange (date) {
    if (minDate && date < sanitizeDate(minDate)) return -1
    if (maxDate && sanitizeDate(maxDate) < date) return 1
    return 0
  }

  /**
   * @param {Date} date
   * @param {string} inputValue
   */
  function dispatchOnChange (date, inputValue) {
    onChange(date, inputValue)
  }

  /**
   * @param {import('./interfaces').Picker=} type
   */
  function showPicker (type) {
    setState({
      currentPicker: type,
      activeDescendant: null
    })
  }

  /**
   * @param {import('./interfaces').Picker=} type
   */
  function hidePicker (type) {
    aboutToHide = type

    setState({
      currentPicker: type === 'day' ? null : 'day',
      activeDescendant: null
    })
  }

  /**
   * @param {RefObject<Element>} ref
   */
  function handleFocusedButtonRef (ref) {
    if (!openOnInputFocus && !openOnIconFocus && ref && ref.current) {
      ref.current.focus()
    }
  }

  function render () {
    const rootClassName = classNames('zb-date-picker-trigger', className, {
      'zb-date-picker-is-error': !!error,
      'zb-date-picker-trigger-is-focused': focus,
      'zb-date-picker-trigger-is-disabled': disabled
    })
    const inputId = id || `${dropdownContainerId}-input`
    const tabIndex = disabled || openOnInputFocus ? '-1' : '0'
    const disabledHandler = disabled ? null : true
    const title = 'Change date'
    const iconProps = {
      name: 'core-date-picker-calendar',
      size: 'small',
      title,
      className: 'zb-date-picker-icon',
      iconProps: props.iconProps
    }
    const comboboxProps = {
      'aria-activedescendant': activeDescendant,
      'aria-expanded': isShown
    }
    const iconWrapProps = {
      ...comboboxProps,
      type: 'button',
      tabIndex,
      'aria-label': title,
      disabled,
      className: iconFocus
        ? classNames('zb-date-picker-icon zb-button-link', {
          'zb-date-picker-trigger-is-focused': iconFocus
        })
        : 'zb-date-picker-icon zb-button-link',
      onClick: disabledHandler && show,
      onFocus: disabledHandler && handleIconFocus,
      onBlur: disabledHandler && handleIconBlur,
      ref: iconRef
    }

    if (isMobile) {
      return renderMobile()
    }

    const inputProps = Object.assign(
      elementAttributes(
        props,
        Object.assign(
          {
            id: inputId,

            disabled,
            readOnly
          },
          openOnInputFocus
            ? {
              ...comboboxProps,
              role: 'combobox',
              'aria-autocomplete': 'list'
            }
            : {}
        )
      ),
      {
        type: 'text',
        className: 'zb-date-picker-input',
        autoComplete: 'off',
        value: displayDate,
        'aria-invalid': !!error,
        'aria-label': ariaLabel,
        onChange: disabledHandler && handleChange,
        onKeyPress: disabledHandler && handleKeyPress,
        onBlur: disabledHandler && handleBlur,
        onFocus: disabledHandler && handleFocus,
        ref: inputRef
      }
    )

    return (
      <div
        ref={containerElementRef}
        className='zb-date-picker-wrapper'
        style={{ position: 'relative', display: 'inline-block' }}
      >
        <span className={rootClassName}>
          <span
            id={`${inputId}-element`}
            className='zb-slider-description-sr-only'
          >
            {dateFormat}
          </span>
          <input
            {...inputProps}
            aria-describedby={inputProps['aria-describedby'] ? inputProps['aria-describedby'] : `${inputId}-element`}
            placeholder={placeholder || dateFormat}
          />
          <button {...iconWrapProps}>
            <Icon {...iconProps} />
          </button>
        </span>
        <DatePickerDropDown
          id={dropdownContainerId}
          isShown={isShown}
          ref={dropdownContainerRef}
          minDate={minDate}
          maxDate={maxDate}
          currentPicker={currentPicker}
          onMonthSelected={handleMonthSelected}
          onYearSelected={handleYearSelected}
          onDaySelected={handleSelect}
          disableDayFn={disableDayFn}
          onYearClick={showYearPicker}
          onMonthClick={showMonthPicker}
          onFocusChanged={handleFocusChanged}
          date={date}
          displayedDate={getShownDate()}
          onFocusedButtonRef={handleFocusedButtonRef}
        />
      </div>
    )
  }

  function renderMobile () {
    const min = minDate ? moment(minDate).format(MOBILE_FORMAT) : ''
    const max = maxDate ? moment(maxDate).format(MOBILE_FORMAT) : ''
    const disabledHandler = disabled ? null : true

    const wrapperClassName = classNames(
      'zb-date-picker-is-mobile-wrapper',
      'zb-date-picker-wrapper',
      className
    )

    const inputClassName = classNames(
      'zb-date-picker-trigger',
      'zb-date-picker-is-mobile',
      'zb-date-picker-input',
      {
        'zb-date-picker-is-error': !!error,
        'zb-date-picker-trigger-is-focused': focus,
        'zb-date-picker-trigger-is-disabled': disabled
      }
    )

    const inputProps = elementAttributes(props, {
      id,
      placeholder,
      disabled,
      required,
      min,
      max
    })

    return (
      <div
        className={wrapperClassName}
        ref={containerElementRef}
        onFocus={disabledHandler && handleFocus}
        onBlur={disabledHandler && handleBlur}
      >
        <input
          {...inputProps}
          type='date'
          className={inputClassName}
          value={displayDate}
          aria-invalid={error}
          onChange={handleChange}
          onFocusCapture={handleInputFocus}
          onBlurCapture={handleInputBlur}
          aria-label='Enter date'
        />
        <Icon
          name='core-date-picker-calendar'
          size='small'
          title='Toggle calendar'
          className='zb-date-picker-icon'
        />
      </div>
    )
  }
}

/**
 * @param {Date=} date
 * @param {Date=} stateDate
 * @returns {boolean}
 */
function hasDateChanged (date, stateDate) {
  if ((!date && stateDate) || (date && !stateDate)) return true
  else if (!date && !stateDate) return false
  return !moment(date).isSame(stateDate, 'day')
}

/**
 * @param {import('react').RefObject<Element>} elementRef
 * @param {import('react').FocusEvent<Element>} event
 * @returns {boolean=}
 */
function isInside ({ current }, { relatedTarget }) {
  return relatedTarget && contains(current, relatedTarget)
}

/**
 * @param {moment.ConfigType=} date
 * @returns {Date}
 */
function sanitizeDate (date) {
  return date && moment(date).startOf('day').toDate()
}

function noop () {}

const DatePicker = forwardRef(datePicker)

DatePicker.displayName = 'DatePicker'

DatePicker.propTypes = {
  date: PropTypes.instanceOf(Date),
  defaultDate: PropTypes.instanceOf(Date),
  initialViewDate: PropTypes.instanceOf(Date),
  dateFormat: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  keepDateInRange: PropTypes.bool,
  disableDayFn: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  customErrorMsg: PropTypes.object,
  openOnInputFocus: PropTypes.bool,
  openOnIconFocus: PropTypes.bool,
  isMobile: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onError: PropTypes.func,
  onInputChange: PropTypes.func,
  onUserChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  iconProps
}

DatePicker.defaultProps = {
  required: false,
  readOnly: false,
  openOnInputFocus: false,
  openOnIconFocus: false,
  isMobile: false,
  keepDateInRange: false,
  placeholder: '',
  scrollIntoView: false,
  disableDayFn: () => false,
  dateFormat: FORMAT,
  customErrorMsg: {},
  onChange: noop,
  onBlur: noop,
  onFocus: noop,
  onError: noop,
  onInputChange: noop,
  onUserChange: noop,
  ariaLabel: 'Enter date'
}

export default DatePicker
export { DatePicker, ALLOWED_FORMATS }
